<template>
  <!-- ===== Hero Section ===== -->
  <section class="hero-section" ref="heroRef">
    <div class="hero-bg-gradient"></div>
    <div class="hero-mesh"></div>

    <!-- イースターエッグ: 出航中の船（ヒーロー相対。heroのoverflow:hiddenで端はクリップされる）
         外側spanが横移動、内側アイコンが波の揺れを担当（X移動を単一イージングに保つ） -->
    <span
      v-if="voyage"
      ref="voyageRef"
      class="voyage-ship"
      :class="voyage.phase"
      :style="voyage.style"
    >
      <v-icon size="15" class="voyage-ship-icon">mdi-sail-boat</v-icon>
    </span>

    <div class="hero-content">
      <div class="hero-badge" ref="badgeRef">
        <button
          ref="shipBtnRef"
          type="button"
          class="badge-ship-btn"
          aria-label="船を出航させる"
          @click="setSail"
        >
          <v-icon
            size="15"
            class="badge-ship"
            :class="{ 'ship-away': voyaging, docked }"
          >mdi-sail-boat</v-icon>
        </button>
        <span class="badge-label" :class="{ 'is-fading': badgeFading }">{{ badgeText }}</span>
      </div>
      <h1 class="hero-title">
        ITで<span class="gradient-text">「真の価値」</span>を<br />届ける
      </h1>
      <p class="hero-subtitle">合同会社エクスシップス — exships LLC.</p>
      <div class="hero-actions">
        <button class="btn-primary" @click="scrollToFeatures">
          強みを見る
          <svg
            class="btn-icon btn-icon--down"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </button>
        <router-link to="/contact" class="btn-ghost">お問い合わせ</router-link>
      </div>
    </div>

    <div class="hero-scroll-hint">
      <div class="scroll-line"></div>
    </div>
  </section>

  <!-- ===== Stats Section（uiverse風ターミナルウィンドウ） ===== -->
  <section class="stats-section" ref="statsRef">
    <v-container>
      <div class="terminal" role="group" aria-label="会社の実績サマリー">
        <div class="terminal-bar">
          <span class="term-dot dot-r" aria-hidden="true"></span>
          <span class="term-dot dot-y" aria-hidden="true"></span>
          <span class="term-dot dot-g" aria-hidden="true"></span>
          <span class="terminal-title">exships — stats</span>
        </div>
        <div class="terminal-body">
          <p class="term-line" :class="{ shown: termLines > 0 }">
            <span class="term-prompt">$</span>
            <span>exships stats --summary</span>
          </p>
          <p
            v-for="(stat, i) in stats"
            :key="stat.label"
            class="term-line"
            :class="{ shown: termLines > i + 1 }"
          >
            <span class="term-key">{{ stat.label }}</span>
            <span class="term-dots" aria-hidden="true"></span>
            <span class="term-value">{{ displayStats[i] }}</span>
          </p>
          <p class="term-line" :class="{ shown: termLines > stats.length + 1 }">
            <span class="term-prompt">$</span>
            <span class="term-cursor" aria-hidden="true"></span>
          </p>
        </div>
      </div>
      <!-- ターミナルの出力に続く「手書きの一言」。ふい字P を12文字にサブセットして同梱。
           ターミナルの最終行が出たあとに1文字ずつ書かれる -->
      <p class="tegaki" :class="{ play: tegakiPlay }" aria-label="そのアイディアを形にする">
        <span
          v-for="(ch, i) in tegakiChars"
          :key="i"
          :style="{ animationDelay: `${i * 0.11}s` }"
          aria-hidden="true"
          >{{ ch }}</span
        >
      </p>
    </v-container>
  </section>

  <!-- ===== Features Section ===== -->
  <section class="features-section" ref="featuresRef">
    <v-container>
      <div class="section-header reveal" v-reveal>
        <span class="section-tag">Our Services</span>
        <h2 class="section-title">私たちの強み</h2>
        <p class="section-desc">最新技術と柔軟な開発力で、お客様のビジネス課題を解決します</p>
      </div>

      <div class="features-grid">
        <div
          v-for="(feature, i) in features"
          :key="i"
          class="feature-card reveal"
          :class="feature.theme"
          v-reveal
        >
          <div class="feature-card-head">
            <div class="feature-icon-wrap">
              <v-icon size="26" class="feature-icon">{{ feature.icon }}</v-icon>
            </div>
            <span class="feature-num">0{{ i + 1 }}</span>
          </div>
          <p class="feature-en">{{ feature.en }}</p>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-desc">{{ feature.description }}</p>
          <ul class="feature-points">
            <li v-for="point in feature.points" :key="point">
              <v-icon size="15" class="feature-point-icon">mdi-check-circle</v-icon>
              <span>{{ point }}</span>
            </li>
          </ul>
          <div class="feature-tags">
            <span v-for="tag in feature.tags" :key="tag" class="feature-tag">{{ tag }}</span>
          </div>
        </div>
      </div>
    </v-container>
  </section>

  <!-- ===== News Section（お知らせ: ブログ更新） ===== -->
  <section class="news-section">
    <v-container>
      <div class="section-header reveal" v-reveal>
        <span class="section-tag">News</span>
        <h2 class="section-title">お知らせ</h2>
      </div>

      <ul class="news-list">
        <li
          v-for="post in newsPosts"
          :key="post.id"
          class="news-item reveal"
          v-reveal
        >
          <router-link
            :to="{ name: 'blog-detail', params: { id: post.id } }"
            class="news-link"
          >
            <time class="news-date" :datetime="post.date">{{
              formatDotDate(post.date)
            }}</time>
            <span class="news-chip">Blog</span>
            <span class="news-title">{{ post.title }}</span>
            <v-icon size="14" class="news-arrow">mdi-arrow-right</v-icon>
          </router-link>
        </li>
      </ul>
    </v-container>
  </section>

  <!-- ===== CTA Section ===== -->
  <section class="cta-section">
    <div class="cta-bg"></div>
    <v-container>
      <div class="cta-content reveal" v-reveal>
        <h2 class="cta-title">
          プロジェクトを<br /><span class="gradient-text">一緒に始めましょう</span>
        </h2>
        <p class="cta-desc">課題や要件をお聞かせください。最適なご提案をいたします。</p>
        <router-link to="/contact" class="btn-primary btn-large">
          無料相談を申し込む
          <svg
            class="btn-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </router-link>
      </div>
    </v-container>
  </section>
</template>

<script>
import { blogPosts } from "@/constants/blogPosts";
import { formatDotDate } from "@/utils/date";
import { vReveal, observeOnce } from "@/composables/useScrollReveal";

// "20+" → { target: 20, suffix: "+" }。数値で始まらないものは null
function parseStatNumber(number) {
  const m = /^(\d+)(.*)$/.exec(number);
  return m ? { target: Number(m[1]), suffix: m[2] } : null;
}

const BADGE_TEXT = "遊び心も、積み荷のうち";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
  directives: { reveal: vReveal },
  data: () => ({
    stats: [
      { number: "5+", label: "Years Experience" },
      { number: "20+", label: "Projects Delivered" },
      { number: "3", label: "Core Services" },
      { number: "100%", label: "Client Focused" },
    ],
    // カウントアップ中の表示値（初期値は0。可視化前はヒーロー下で見えない）
    displayStats: [],
    // ターミナルの表示済み行数（プロンプト+出力4行+末尾プロンプト）
    termLines: 0,
    // ターミナル直下の手書き一行。文字単位で描くため配列に分割
    tegakiChars: Array.from("そのアイディアを形にする"),
    tegakiPlay: false,
    // お知らせ: 執筆日（front-matterのdate）の新しい順に5件
    newsPosts: [...blogPosts]
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 5),
    stopStatsObserver: null,
    // イースターエッグ（船の出航）の状態
    badgeText: BADGE_TEXT,
    badgeFading: false,
    sailing: false, // 多重発火防止ロック
    voyaging: false, // バッジ内の船を隠す（reduced-motion時は隠さない）
    voyage: null, // { phase: "depart"|"return", style: {...} }
    docked: false, // 帰港後、バッジの揺れをニュートラル位相から再開させる
    features: [
      {
        icon: "mdi-cellphone-link",
        title: "クロスプラットフォーム開発",
        en: "Cross-Platform Development",
        description:
          "iOS / Android / Web を横断し、企画から運用まで一気通貫で支援。少人数チームでも高品質かつスピーディーなリリースを実現します。",
        points: [
          "ネイティブ品質にこだわったUI/UX",
          "Kotlin MultiplatformによるiOS / Android同時開発",
          "リリース後の運用・グロースまで伴走",
        ],
        tags: ["Swift", "Kotlin", "Flutter", "Vue"],
        theme: "feat-navy",
      },
      {
        icon: "mdi-brain",
        title: "AI・機械学習",
        en: "AI & Machine Learning",
        description:
          "LLMを活用したAIエージェント開発から、AI駆動開発による生産性向上まで。現場で本当に使えるAI活用をご提案します。",
        points: [
          "LLM / RAGを用いたシステム構築",
          "業務を自動化するAIエージェント開発",
          "AI駆動開発の導入・定着支援",
        ],
        tags: ["LLM", "RAG", "Python", "Codex", "Claude Code"],
        theme: "feat-terra",
      },
      {
        icon: "mdi-chart-bubble",
        title: "チーム開発支援",
        en: "Team Development",
        description:
          "アジャイル・スクラムの導入から、CI/CDによる開発基盤づくりまで。チームが自走できる仕組みを構築します。",
        points: [
          "スクラム導入・改善の伴走支援",
          "CI/CDパイプラインの構築・整備",
          "ふりかえり文化の定着サポート",
        ],
        tags: ["Agile", "Scrum", "CI/CD"],
        theme: "feat-olive",
      },
    ],
  }),
  created() {
    this.displayStats = this.stats.map((s) =>
      parseStatNumber(s.number) ? `0${parseStatNumber(s.number).suffix}` : s.number
    );
  },
  mounted() {
    // ターミナルが見えたら行を順次表示（reduced-motion 時は即全表示・即最終値）
    this.stopStatsObserver = observeOnce(this.$refs.statsRef, this.runTerminal);
  },
  beforeUnmount() {
    this.stopStatsObserver?.();
  },
  methods: {
    formatDotDate,
    // ターミナルの行を1行ずつ表示する。reduced-motion 時は即全表示
    runTerminal() {
      const total = this.stats.length + 2; // プロンプト + 出力4行 + 末尾プロンプト
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.termLines = total;
        this.displayStats = this.stats.map((s) => s.number);
        this.tegakiPlay = true;
        return;
      }
      const tick = () => {
        this.termLines += 1;
        if (this.termLines === 2) this.animateStats(); // 最初の出力行と同時にカウント開始
        if (this.termLines < total) {
          setTimeout(tick, 220);
        } else {
          // 打ち終わって一呼吸おいてから、手書きの一言を書き始める
          setTimeout(() => (this.tegakiPlay = true), 300);
        }
      };
      tick();
    },
    // バッジ文言をフェードで差し替える
    async swapBadgeText(text) {
      this.badgeFading = true;
      await wait(250);
      this.badgeText = text;
      this.badgeFading = false;
      await wait(250);
    },
    // 航海(voyage-x)のアニメ完了を待つ。固定タイマーだとCSSアニメと数フレーム
    // ずれて最終位置へスナップして見えるため、animationendに同期する
    waitVoyageEnd(timeoutMs) {
      const el = this.$refs.voyageRef;
      if (!el) return wait(timeoutMs);
      return new Promise((resolve) => {
        const finish = () => {
          el.removeEventListener("animationend", onEnd);
          clearTimeout(timer);
          resolve();
        };
        const onEnd = (e) => {
          // 子アイコンの voyage-bob の完了は無視（scoped名はハッシュ付きなので前方一致）
          if (!e.animationName.startsWith("voyage-x")) return;
          finish();
        };
        el.addEventListener("animationend", onEnd);
        const timer = setTimeout(finish, timeoutMs); // アニメが動かない環境の保険
      });
    },
    // イースターエッグ: 船がバッジから出航し、ひと回りして帰港する
    async setSail() {
      if (this.sailing) return;
      this.sailing = true;

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        // 動きは出さず、文言だけ挨拶して戻す
        await this.swapBadgeText("いってらっしゃい");
        await wait(2000);
        await this.swapBadgeText(BADGE_TEXT);
        this.sailing = false;
        return;
      }

      const hero = this.$refs.heroRef;
      // 座標基準はアイコンではなくボタン。アイコンは入港/揺れアニメのtransformで
      // 動くため、クリック時点の位相によって測定がぶれて着岸位置がずれる
      const shipBtn = this.$refs.shipBtnRef;
      const badge = this.$refs.badgeRef;
      if (!hero || !shipBtn || !badge) {
        this.sailing = false;
        return;
      }
      const heroRect = hero.getBoundingClientRect();
      const shipRect = shipBtn.getBoundingClientRect();
      const startLeft = shipRect.left - heroRect.left;
      const startTop = shipRect.top - heroRect.top;

      // 文言切替でバッジ幅が変わらないよう現在幅で固定
      // （offsetWidthは整数に切り捨てられ実幅より狭くなり折り返しの原因になるため、切り上げる）
      badge.style.width = `${Math.ceil(badge.getBoundingClientRect().width)}px`;

      // 出航: バッジ位置 → ヒーロー左端の外へ
      this.voyaging = true;
      this.docked = false;
      this.voyage = {
        phase: "depart",
        style: {
          left: `${startLeft}px`,
          top: `${startTop}px`,
          "--dist": `${-(startLeft + 40)}px`,
        },
      };
      this.swapBadgeText("ただいま出航中…");
      await this.$nextTick();
      await this.waitVoyageEnd(2600);
      this.voyage = null;

      // 沖合を航海中
      await wait(1200);

      // 帰港: ヒーロー左端の外 → バッジ定位置
      this.voyage = {
        phase: "return",
        style: {
          left: "-40px",
          top: `${startTop}px`,
          "--dist": `${startLeft + 40}px`,
        },
      };
      await this.$nextTick();
      await this.waitVoyageEnd(2600);

      // 船は着岸位置(fill:forwards)に留めたまま、先に文言と幅を元へ戻す。
      // バッジは中央寄せのため、文言が短いままアイコンを交代すると
      // ボタン位置が着岸点からずれてカクつく
      await this.swapBadgeText(BADGE_TEXT);
      badge.style.width = "";
      await this.$nextTick();

      // 交代: 航海の揺れは2周期でちょうどニュートラルに戻っており、docked で
      // バッジ側の揺れも位相0(ニュートラル)から再開するため、姿勢が途切れない
      this.docked = true;
      this.voyaging = false;
      this.voyage = null;
      this.sailing = false;
    },
    scrollToFeatures() {
      const el = this.$refs.featuresRef;
      if (!el) return;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
    },
    animateStats() {
      const parsed = this.stats.map((s) => parseStatNumber(s.number));
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        this.displayStats = this.stats.map((s) => s.number);
        return;
      }
      const DURATION = 1200;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / DURATION, 1);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        this.displayStats = this.stats.map((s, i) => {
          const p = parsed[i];
          if (!p) return s.number;
          return `${Math.round(p.target * eased)}${p.suffix}`;
        });
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

/* ===== Hero ===== */
.hero-section {
  position: relative;
  height: 100vh;
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: var(--c-bg);
  /* 子要素のパララックスが参照する名前付きビュータイムライン */
  view-timeline: --hero block;
}

.hero-bg-gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 90% 70% at 50% 0%, var(--c-accent-soft) 0%, transparent 72%);
}

.hero-mesh {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(var(--c-mesh) 1px, transparent 1px),
    linear-gradient(90deg, var(--c-mesh) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 24px;
}

@media (prefers-reduced-motion: no-preference) {
  .hero-content {
    animation: hero-enter 1s ease 0.3s both;
  }
}
@keyframes hero-enter {
  from {
    opacity: 0;
    transform: translateY(32px);
  }
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--c-accent-soft);
  border: 1px solid var(--c-accent-border);
  color: var(--c-accent-text);
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  padding: 6px 16px;
  border-radius: 100px;
  margin-bottom: 28px;
  /* 入港してくる船アイコンをバッジの丸枠内でクリップする */
  position: relative;
  overflow: hidden;
  /* 出航中の文言切替時は幅をJSで固定するため、中央寄せにしておく */
  justify-content: center;
}

/* 船アイコンのクリック領域（イースターエッグ発動ボタン） */
.badge-ship-btn {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: inherit;
  cursor: pointer;
}
.badge-ship-btn:focus-visible {
  outline: 2px solid var(--c-accent-text);
  outline-offset: 2px;
  border-radius: 4px;
}

.badge-label {
  transition: opacity 0.25s ease;
  /* 幅固定中に1px未満の誤差で折り返さないように */
  white-space: nowrap;
}
.badge-label.is-fading {
  opacity: 0;
}

/* 船(exships)がバッジ右端から定位置へ入港し、以後ゆるやかに揺れる */
.badge-ship {
  color: var(--c-accent-text) !important;
}
/* 出航中はバッジ内の船を隠す（アニメを止めないよう visibility で） */
.badge-ship.ship-away {
  visibility: hidden;
}
@media (prefers-reduced-motion: no-preference) {
  .badge-ship {
    animation:
      ship-sail-in 1.6s cubic-bezier(0.22, 1, 0.36, 1) 1s both,
      ship-bob 3.2s ease-in-out 2.6s infinite;
  }
  /* 帰港直後: 入港アニメを外し、揺れをニュートラル位相(0%)から再開する。
     帰港した航海の船もニュートラル姿勢で終わるため、切り替えが滑らかになる */
  .badge-ship.docked {
    animation: ship-bob 3.2s ease-in-out infinite;
  }
}
@keyframes ship-sail-in {
  from {
    transform: translateX(240px);
  }
  to {
    transform: translateX(0);
  }
}
@keyframes ship-bob {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-1.5px) rotate(-4deg);
  }
}

/* ===== イースターエッグ: 出航する船 =====
   位置(left/top)と移動距離(--dist)は setSail() がインラインで与える。
   横移動(外側span)は単一イージングの translateX のみ、波の揺れ(内側アイコン)は
   別アニメーションに分離して、移動速度が段付きにならないようにする。
   reduced-motion時はJS側が船を出さないが、念のためアニメ定義もガードする */
.voyage-ship {
  position: absolute;
  z-index: 3;
  display: inline-flex;
  pointer-events: none;
}
.voyage-ship-icon {
  color: var(--c-accent-text) !important;
}
@media (prefers-reduced-motion: no-preference) {
  .voyage-ship.depart {
    animation: voyage-x 2s cubic-bezier(0.55, 0, 0.8, 0.5) forwards; /* 加速して出ていく */
  }
  .voyage-ship.return {
    animation: voyage-x 2s cubic-bezier(0.22, 1, 0.36, 1) forwards; /* 減速して着岸 */
  }
  .voyage-ship .voyage-ship-icon {
    animation: voyage-bob 1.1s ease-in-out infinite;
  }
  /* 帰港中は移動2sに対して揺れ1s×2周期。着岸の瞬間にちょうど
     ニュートラル姿勢(translateY 0 / rotate 0)で揺れが終わる */
  .voyage-ship.return .voyage-ship-icon {
    animation: voyage-bob 1s ease-in-out 2;
  }
}
@keyframes voyage-x {
  to {
    transform: translateX(var(--dist));
  }
}
@keyframes voyage-bob {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-2px) rotate(-5deg);
  }
}

.hero-title {
  font-size: clamp(2.4rem, 6vw, 4rem);
  font-weight: 800;
  color: var(--c-text-1);
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin-bottom: 20px;
}

.gradient-text {
  color: var(--c-accent-text);
}

/* 「真の価値」をブラーから浮かび上がらせる。
   親の hero-enter(0.3s〜1.3s)が落ち着き始めるタイミングで遅れて合焦する */
@media (prefers-reduced-motion: no-preference) {
  .hero-title .gradient-text {
    display: inline-block; /* filter / transform を効かせるため */
    animation: focus-in 1.3s ease-out 0.75s both;
  }
}
@keyframes focus-in {
  from {
    opacity: 0;
    filter: blur(16px);
    transform: scale(1.06);
  }
  55% {
    opacity: 1;
  }
  to {
    opacity: 1;
    filter: blur(0);
    transform: scale(1);
  }
}

.hero-subtitle {
  font-size: 1rem;
  color: var(--c-text-3);
  letter-spacing: 0.05em;
  margin-bottom: 40px;
}

.hero-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

/* .btn-primary / .btn-ghost / .btn-large は src/assets/buttons.css の共通定義を使う */

.hero-scroll-hint {
  position: absolute;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
}
@media (prefers-reduced-motion: no-preference) {
  .hero-scroll-hint {
    animation: hint-enter 1.2s ease 1.4s both;
  }
}
@keyframes hint-enter {
  from { opacity: 0; }
}
.scroll-line {
  width: 1px;
  height: 56px;
  background: linear-gradient(to bottom, transparent, var(--c-accent-border), transparent);
  margin: 0 auto;
  animation: scrollPulse 2.5s ease-in-out infinite;
}
@keyframes scrollPulse {
  0%, 100% { opacity: 0.3; transform: scaleY(1); }
  50% { opacity: 1; transform: scaleY(1.1); }
}

/* ===== Stats Section ===== */
.stats-section {
  background: var(--c-bg-alt);
  padding: 60px 0;
  border-top: 1px solid var(--c-border-subtle);
  border-bottom: 1px solid var(--c-border-subtle);
}

/* uiverse.io のターミナルカード系パターン(MIT)をデザイントークンに合わせて移植。
   ターミナルは両テーマ共通でダークネイビー地に固定する */
.terminal {
  max-width: 620px;
  margin: 0 auto;
  background: #1c2438;
  border: 1px solid rgba(247, 242, 234, 0.1);
  border-radius: 14px;
  box-shadow: 0 12px 40px var(--c-shadow-card);
  overflow: hidden;
  text-align: left;
}

.terminal-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(247, 242, 234, 0.1);
}

.term-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
}
.dot-r { background: #d9756a; }
.dot-y { background: #ddb56f; }
.dot-g { background: #93b878; }

.terminal-title {
  margin-left: 8px;
  font-family: "Fira Code", "SF Mono", Menlo, Consolas, "Cascadia Code", monospace;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: rgba(247, 242, 234, 0.55);
}

.terminal-body {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 18px 20px 20px;
  font-family: "Fira Code", "SF Mono", Menlo, Consolas, "Cascadia Code", monospace;
  font-size: 0.85rem;
  color: #e9e4d8;
}

.term-line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 0;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.term-line.shown {
  opacity: 1;
  transform: none;
}

.term-prompt {
  color: #d99a6f;
  user-select: none;
}

.term-key {
  color: rgba(247, 242, 234, 0.6);
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* key と value をつなぐリーダー線 */
.term-dots {
  flex: 1;
  border-bottom: 1px dashed rgba(247, 242, 234, 0.22);
  transform: translateY(-3px);
}

.term-value {
  font-weight: 700;
  color: #fffdf8;
  /* カウントアップ中の桁伸縮で横揺れしないよう等幅数字にする */
  font-variant-numeric: tabular-nums;
}

.term-cursor {
  display: inline-block;
  width: 8px;
  height: 0.95em;
  background: #e9e4d8;
  transform: translateY(2px);
  animation: term-blink 1.1s steps(1) infinite;
}
@keyframes term-blink {
  50% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .term-line {
    transition: none;
  }
  .term-cursor {
    animation: none;
  }
}

/* ===== 手書きの一言（ターミナル直下） =====
   ふい字P（HuiFontP、ふい氏のフリーフォント）を「そのアイディアを形にする」の12文字
   のみにサブセット化して同梱(約2.6KB)。4KiB未満なのでビルド時に data URI へ inline される。
   文言を変える場合は元のTTFから pyftsubset で再サブセットが必要 */
@font-face {
  font-family: "HuiFontP";
  src: url("@/assets/fonts/HuiFontP-subset.woff2") format("woff2");
  font-display: block;
}

.tegaki {
  --tegaki-dur: 0.16s; /* 1文字を書く時間（文字間の間隔 0.11s はテンプレート側） */
  max-width: 620px; /* .terminal と同じ幅に揃え、右下に書き添えたメモのように見せる */
  margin: 28px auto 0;
  font-family: "HuiFontP", var(--font-heading), sans-serif;
  font-size: clamp(1.6rem, 4.2vw, 2.6rem);
  line-height: 1.3;
  letter-spacing: 0.04em;
  color: var(--c-text-1);
  white-space: nowrap;
  text-align: right;
  transform: rotate(-2.5deg); /* 行全体を直線のまま右肩上がりに */
  transform-origin: right center;
}

/* 各文字は右側を clip して隠し、左から書き進めるように開く */
.tegaki span {
  display: inline-block;
  opacity: 0;
  clip-path: inset(-15% 100% -15% -5%);
}

.tegaki.play span {
  animation: tegaki-kaku var(--tegaki-dur) ease-out forwards;
}

@keyframes tegaki-kaku {
  from {
    opacity: 1;
    clip-path: inset(-15% 100% -15% -5%);
  }
  to {
    opacity: 1;
    clip-path: inset(-15% -5% -15% -5%);
  }
}

@media (max-width: 600px) {
  .tegaki {
    margin-top: 22px;
    text-align: center;
    transform-origin: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tegaki span {
    opacity: 1;
    clip-path: none;
    animation: none;
  }
}

/* ===== Features Section ===== */
.features-section {
  background: var(--c-bg);
  padding: 100px 0;
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-tag {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--c-accent-text);
  background: var(--c-accent-soft);
  border: 1px solid var(--c-accent-border);
  padding: 4px 14px;
  border-radius: 100px;
  margin-bottom: 16px;
}

.section-title {
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  font-weight: 800;
  color: var(--c-text-1);
  letter-spacing: -0.02em;
  margin-bottom: 16px;
}

.section-desc {
  font-size: 1rem;
  color: var(--c-text-4);
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.7;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
@media (max-width: 900px) {
  .features-grid { grid-template-columns: 1fr; max-width: 480px; margin: 0 auto; }
}

.feature-card {
  --feat: var(--c-accent);
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--c-bg-card);
  border: 1px solid var(--c-border);
  /* 上線は淡い下地にしておき、ホバー時に ::before の本来色ラインが中央から広がる */
  border-top: 3px solid color-mix(in srgb, var(--feat) 25%, transparent);
  border-radius: 20px;
  padding: 30px 28px 28px;
  transition: border-color 0.3s ease, background 0.3s ease,
    transform 0.3s ease, box-shadow 0.3s ease;
}

/* ホバー時、上部のカラー線が中央から左右へ広がる */
.feature-card::before {
  content: "";
  position: absolute;
  top: -3px; /* border-top と同じ位置に重ねる */
  left: 18px;
  right: 18px; /* 角丸(20px)のカーブ部分を避けた直線区間だけに敷く */
  height: 3px;
  border-radius: 2px;
  background: var(--feat);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  pointer-events: none;
}
.feature-card:hover::before,
.feature-card:focus-visible::before {
  transform: scaleX(1);
}
@media (prefers-reduced-motion: reduce) {
  .feature-card::before {
    transition: none;
  }
}
.feature-card:hover {
  background: var(--c-card-hover);
  border-color: color-mix(in srgb, var(--feat) 45%, transparent);
  /* 上線の下地は変えない（本来色は ::before のラインが中央から広がって表現する） */
  border-top-color: color-mix(in srgb, var(--feat) 25%, transparent);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px var(--c-shadow-card);
}

/* カードごとのアクセント（暖色パレットに合わせた3色） */
.feat-navy { --feat: #28344f; }
.feat-terra { --feat: #a8542c; }
.feat-olive { --feat: #5a6b3b; }
:global(html.dark .feat-navy) { --feat: #9fb1d6; }
:global(html.dark .feat-terra) { --feat: #d99a6f; }
:global(html.dark .feat-olive) { --feat: #aab578; }

.feature-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.feature-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--feat) 11%, transparent);
  border: 1px solid color-mix(in srgb, var(--feat) 26%, transparent);
}

.feature-icon {
  color: var(--feat) !important;
}

.feature-num {
  font-family: var(--font-heading);
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
  color: color-mix(in srgb, var(--feat) 22%, transparent);
  user-select: none;
}

.feature-en {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--feat);
  margin-bottom: 6px;
}

.feature-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--c-text-2);
  margin-bottom: 12px;
  letter-spacing: -0.01em;
}

.feature-desc {
  font-size: 0.9rem;
  color: var(--c-text-4);
  line-height: 1.75;
  margin-bottom: 18px;
}

.feature-points {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  border-top: 1px dashed var(--c-border);
  padding-top: 16px;
  flex: 1;
}

.feature-points li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.84rem;
  font-weight: 500;
  color: var(--c-text-3);
  line-height: 1.6;
}

.feature-point-icon {
  color: var(--feat) !important;
  margin-top: 3px;
  flex-shrink: 0;
}

.feature-tags { display: flex; gap: 8px; flex-wrap: wrap; }

.feature-tag {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--c-text-3);
  background: var(--c-bg-card);
  border: 1px solid var(--c-border);
  padding: 3px 10px;
  border-radius: 100px;
  letter-spacing: 0.04em;
}

/* ===== News Section ===== */
.news-section {
  background: var(--c-bg-alt);
  padding: 100px 0;
  border-top: 1px solid var(--c-border-subtle);
  border-bottom: 1px solid var(--c-border-subtle);
}

.news-list {
  max-width: 760px;
  margin: 0 auto;
  padding: 0;
  list-style: none;
}

.news-item {
  border-bottom: 1px solid var(--c-border-subtle);
}
.news-item:first-child {
  border-top: 1px solid var(--c-border-subtle);
}

.news-link {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 10px;
  text-decoration: none;
  color: inherit;
  text-align: left; /* #app のグローバル中央寄せを打ち消す */
  transition: background 0.2s ease;
}
.news-link:hover,
.news-link:focus-visible {
  background: var(--c-bg-card);
}
.news-link:focus-visible {
  outline: 2px solid var(--c-accent-text);
  outline-offset: -2px;
}

.news-date {
  font-size: 0.8rem;
  color: var(--c-text-5);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.news-chip {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--c-accent-text);
  background: var(--c-accent-soft);
  border: 1px solid var(--c-accent-border);
  padding: 2px 10px;
  border-radius: 100px;
  flex-shrink: 0;
}

.news-title {
  flex: 1;
  min-width: 0;
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--c-text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;
}
.news-link:hover .news-title {
  color: var(--c-accent-text);
}

.news-arrow {
  color: var(--c-text-5) !important;
  flex-shrink: 0;
  transition: transform 0.2s ease, color 0.2s ease;
}
.news-link:hover .news-arrow {
  transform: translateX(4px);
  color: var(--c-accent-text) !important;
}

@media (max-width: 600px) {
  .news-link {
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .news-title {
    flex-basis: 100%;
    white-space: normal;
  }
}

/* ===== CTA Section ===== */
.cta-section {
  position: relative;
  padding: 120px 0;
  background: var(--c-bg-alt);
  overflow: hidden;
  text-align: center;
}

.cta-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 70% 70% at 50% 50%, var(--c-accent-soft), transparent);
}

.cta-content {
  position: relative;
  z-index: 1;
}

.cta-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  color: var(--c-text-1);
  letter-spacing: -0.02em;
  line-height: 1.25;
  margin-bottom: 20px;
}

.cta-desc {
  font-size: 1rem;
  color: var(--c-text-4);
  margin-bottom: 40px;
  line-height: 1.7;
}

/* ===== スクロール駆動アニメーション =====
   animation-timeline: view() でコンポジタ動作。非対応ブラウザは
   @supports で除外され、コンテンツは最初から表示される。 */
@supports (animation-timeline: view()) {
  @media (prefers-reduced-motion: no-preference) {
    /* --- ヒーローのパララックス（ヒーローが画面外へ出る間だけ動く） --- */
    .hero-bg-gradient,
    .hero-mesh {
      animation: hero-parallax-bg linear both;
      animation-timeline: --hero;
      animation-range: exit 0% exit 100%;
    }
    .hero-content {
      /* 時間ベースの登場アニメと、スクロール連動のドリフトを併用。
         登場側は opacity/transform、ドリフト側は translate のみを
         動かすので互いに干渉しない。 */
      animation:
        hero-enter 1s ease 0.3s both,
        hero-parallax-content linear both;
      animation-timeline: auto, --hero;
      animation-range: normal, exit 0% exit 100%;
    }

    /* --- セクションのフェードイン（ビューポート進入に連動） --- */
    .section-header,
    .feature-card,
    .news-item,
    .cta-content {
      animation: rise-in linear both;
      animation-timeline: view();
      animation-range: entry 10% cover 30%;
    }
    /* 横並び要素はレンジをずらしてスタガー */
    .feature-card:nth-child(2) { animation-range: entry 18% cover 38%; }
    .feature-card:nth-child(3) { animation-range: entry 26% cover 46%; }

    /* 最終セクションはページ末尾でも完走できるよう entry のみで完結させる */
    .cta-content { animation-range: entry 10% entry 90%; }
  }
}

/* ===== IntersectionObserver フォールバック =====
   animation-timeline 非対応ブラウザ（Safari等）向け。対応ブラウザでは
   @supports not によりこのブロックが丸ごと無効になり、上のスクロール駆動
   アニメーションとは二重発火しない。reduced-motion 時も無効（即表示）。
   .revealed の付与は composables/useScrollReveal.js の v-reveal が行う。 */
@supports not (animation-timeline: view()) {
  @media (prefers-reduced-motion: no-preference) {
    .reveal {
      opacity: 0;
      transform: translateY(28px);
      transition:
        opacity 0.7s ease,
        transform 0.7s ease,
        /* App.vue の全要素テーマ遷移(color系0.3s)を上書きしてしまうため再宣言 */
        background-color 0.3s ease,
        border-color 0.3s ease,
        color 0.3s ease;
    }
    .reveal.revealed {
      opacity: 1;
      transform: none;
    }
    /* 並び要素は遅延をずらしてスタガー */
    .feature-card.reveal:nth-child(2),
    .news-item.reveal:nth-child(2) { transition-delay: 0.08s; }
    .feature-card.reveal:nth-child(3),
    .news-item.reveal:nth-child(3) { transition-delay: 0.16s; }
    .news-item.reveal:nth-child(4) { transition-delay: 0.24s; }
    .news-item.reveal:nth-child(5) { transition-delay: 0.32s; }
  }
}

@keyframes hero-parallax-bg {
  to {
    translate: 0 30%;
    opacity: 0.35;
  }
}
@keyframes hero-parallax-content {
  to {
    translate: 0 90px;
  }
}
@keyframes rise-in {
  from {
    opacity: 0;
    transform: translateY(28px);
  }
}
</style>
